import type {
  FieldDefinition,
  MetricDefinition,
} from "@/hooks/api/use-field-definitions";
import type { ColumnReference } from "@/types/formula-column";
import type { FormulaParser } from "./formula-parser";
import {
  type ASTNode,
  ASTNodeType,
  type BinaryOperationNode,
  type FunctionCallNode,
  type UnaryOperationNode,
  type VariableNode,
} from "./formula-parser";

/**
 * Formula context for autocomplete
 */
export interface FormulaContext {
  type: "root" | "function" | "parameter";
  position: number;
  text: string;
  isInsideFunction: boolean;
  functionName?: string;
  currentParameterIndex?: number;
}

/**
 * Function signature for autocomplete hints
 */
export interface FunctionSignature {
  name: string;
  parameters: Array<{
    name: string;
    type: string;
    description?: string;
    optional?: boolean;
  }>;
  description?: string;
}

/**
 * Service for validating formulas and providing formula building metadata.
 * Uses the AST-based FormulaParser for syntax validation.
 */
export class FormulaValidationService {
  private parser: FormulaParser;

  private static readonly FUNCTION_SIGNATURES: Record<
    string,
    FunctionSignature
  > = {
    SUM: {
      name: "SUM",
      parameters: [
        { name: "values", type: "number[]", description: "Numbers to sum" },
      ],
      description: "Adds all numbers in a range",
    },
    AVERAGE: {
      name: "AVERAGE",
      parameters: [
        { name: "values", type: "number[]", description: "Numbers to average" },
      ],
      description: "Calculates the mean of numbers",
    },
    IF: {
      name: "IF",
      parameters: [
        {
          name: "condition",
          type: "boolean",
          description: "Condition to test",
        },
        {
          name: "valueIfTrue",
          type: "any",
          description: "Value if condition is true",
        },
        {
          name: "valueIfFalse",
          type: "any",
          description: "Value if condition is false",
          optional: true,
        },
      ],
      description: "Returns one value if condition is true, another if false",
    },
    ROUND: {
      name: "ROUND",
      parameters: [
        { name: "number", type: "number", description: "Number to round" },
        {
          name: "digits",
          type: "number",
          description: "Number of decimal places",
          optional: true,
        },
      ],
      description: "Rounds a number to specified decimal places",
    },
  };

  constructor(parser: FormulaParser) {
    this.parser = parser;
  }

  /**
   * Validate a formula and return validation result
   */
  validateFormula(formula: string): { isValid: boolean; error?: string } {
    if (formula && !formula.startsWith("=")) {
      // <- new validation for missing equal
      return {
        isValid: false,
        error: "Formula must start with an equals sign (=)",
      };
    }
    return this.parser.validate(formula);
  }

  /**
   * Parse formula context for autocomplete
   */
  parseFormulaContext(formula: string, cursorPosition: number): FormulaContext {
    const cleanFormula = formula.startsWith("=") ? formula.slice(1) : formula;
    const adjustedPosition = formula.startsWith("=")
      ? cursorPosition - 1
      : cursorPosition;

    // Basic implementation - can be enhanced
    const beforeCursor = cleanFormula.substring(
      0,
      Math.max(0, adjustedPosition),
    );

    // Check if we're inside a function
    const functionMatch = beforeCursor.match(/([A-Z_][A-Z0-9_]*)\s*\([^)]*$/i);

    if (functionMatch) {
      const functionName = functionMatch[1].toUpperCase();
      const parameterText = beforeCursor.substring(
        functionMatch.index! + functionMatch[1].length + 1,
      );
      const parameterIndex = (parameterText.match(/,/g) || []).length;

      return {
        type: "function",
        position: adjustedPosition,
        text: cleanFormula,
        isInsideFunction: true,
        functionName,
        currentParameterIndex: parameterIndex,
      };
    }

    return {
      type: "root",
      position: adjustedPosition,
      text: cleanFormula,
      isInsideFunction: false,
    };
  }

  /**
   * Get function signature for autocomplete hints
   */
  getFunctionSignature(functionName: string): FunctionSignature | undefined {
    return FormulaValidationService.FUNCTION_SIGNATURES[
      functionName.toUpperCase()
    ];
  }

  /**
   * Get available column references for formula building
   * Dynamically generated from field definitions and available metrics
   */
  getColumnReferences(
    fieldDefs?: FieldDefinition[],
    availableMetrics?: MetricDefinition[],
  ): ColumnReference[] {
    const references: ColumnReference[] = [];

    // Add base campaign fields from field definitions
    if (fieldDefs) {
      fieldDefs
        .filter((field) => !field.is_custom) // Only base fields (not custom fields)
        .forEach((field) => {
          // Map server data types to formula data types
          const formulaDataType = this.mapDataTypeToFormulaType(
            field.data_type,
          );

          references.push({
            key: field.id,
            displayName: field.display_name,
            dataType: formulaDataType,
            category: "base",
            description: `${field.display_name} field`,
          });
        });
    }

    // Add metrics with proper prefix
    if (availableMetrics) {
      availableMetrics.forEach((metric) => {
        references.push({
          key: `metrics.${metric.key}`,
          displayName: metric.displayName,
          dataType: metric.dataType,
          category: "metric",
          description: metric.description || `${metric.displayName} metric`,
        });
      });
    }

    return references;
  }

  /**
   * Map server data types to formula-compatible data types
   */
  private mapDataTypeToFormulaType(serverDataType: string): string {
    const typeMap: Record<string, string> = {
      TEXT: "string",
      text: "string",
      string: "string",
      varchar: "string",
      char: "string",
      NUMBER: "number",
      number: "number",
      int: "number",
      integer: "number",
      float: "number",
      decimal: "number",
      numeric: "number",
      BOOLEAN: "boolean",
      boolean: "boolean",
      bool: "boolean",
      DATE: "date",
      date: "date",
      datetime: "date",
      timestamp: "date",
      DATETIME: "date",
      currency: "currency",
      money: "currency",
    };

    return typeMap[serverDataType] || "string"; // Default to string for unknown types
  }

  /**
   * Get available functions for formula building
   */
  getAvailableFunctions(): string[] {
    return [
      // Core Mathematical
      "SUM",
      "AVERAGE",
      "COUNT",
      "MAX",
      "MIN",
      "ROUND",
      "ABS",
      "CEILING",
      "FLOOR",
      "POWER",
      "SQRT",

      // Logical
      "IF",
      "AND",
      "OR",
      "NOT",
    ];
  }

  /**
   * Extract all variable names from a formula
   */
  private extractVariables(ast: ASTNode): string[] {
    const variables: string[] = [];

    const traverse = (node: ASTNode) => {
      // Type guard to check if node is a valid AST node
      if (!node || typeof node !== "object" || !("type" in node)) {
        return;
      }

      const astNode = node as { type: unknown; [key: string]: unknown };

      switch (astNode.type) {
        case ASTNodeType.VARIABLE: {
          const variableNode = astNode as unknown as VariableNode;
          if (variableNode.name && typeof variableNode.name === "string") {
            variables.push(variableNode.name.toUpperCase());
          }
          break;
        }
        case ASTNodeType.BINARY_OPERATION: {
          const binaryNode = astNode as unknown as BinaryOperationNode;
          if (binaryNode.left && binaryNode.right) {
            traverse(binaryNode.left);
            traverse(binaryNode.right);
          }
          break;
        }
        case ASTNodeType.UNARY_OPERATION: {
          const unaryNode = astNode as unknown as UnaryOperationNode;
          if (unaryNode.operand) {
            traverse(unaryNode.operand);
          }
          break;
        }
        case ASTNodeType.FUNCTION_CALL: {
          const functionNode = astNode as unknown as FunctionCallNode;
          if (functionNode.arguments && Array.isArray(functionNode.arguments)) {
            functionNode.arguments.forEach(traverse);
          }
          break;
        }
        // Skip other node types (LITERAL, etc.)
      }
    };

    traverse(ast);
    return [...new Set(variables)]; // Remove duplicates
  }

  /**
   * Validate that all variables in a formula are recognized by the system
   */
  validateFormulaVariables(
    formula: string,
    columnReferences: ColumnReference[],
  ): { isValid: boolean; unknownVariables?: string[] } {
    if (!formula.trim()) {
      return { isValid: true };
    }

    // Parse the formula to get AST
    const parseResult = this.parser.parse(formula);
    if (!parseResult.isValid || !parseResult.ast) {
      // If formula doesn't parse, let the syntax validation handle it
      return { isValid: true };
    }

    // Extract all variables from the AST
    const usedVariables = this.extractVariables(parseResult.ast);

    // Create a set of all valid variable names (uppercase)
    const validVariables = new Set(
      columnReferences.map((col) => col.key.toUpperCase()),
    );

    // Check for unknown variables
    const unknownVariables = usedVariables.filter(
      (variable) => !validVariables.has(variable),
    );

    return {
      isValid: unknownVariables.length === 0,
      unknownVariables:
        unknownVariables.length > 0 ? unknownVariables : undefined,
    };
  }
}
